/**
 * Risk Weekly Report — module settings.
 *
 * Colours, labels and SLA vocabulary are mirrored from the backend configuration
 * (Configuration/RiskWeekly/departments.json) so the ported report stays visually
 * recognisable against the Python original. The backend also sends label and colour with
 * each department series; these are the fallbacks for anything it does not send.
 */

export const RISK_WEEKLY_MODULE_PATH = 'risk-weekly-report';

/**
 * The views of the original Streamlit report, plus Statistics. Live Dashboard, Explore and
 * Statistics ship (Explore was re-enabled after the Live-Dashboard-only cut; its GetExploreCube
 * and GetCalendar endpoints are live on the server). The other four stay disabled.
 */
export const views = {
  liveDashboard: 'live-dashboard',
  // DISABLED - RMT8993, 2026-09-14: the first production release ships without these four.
  // See docs/report_docs/13-live-dashboard-only.md.
  // grmtUnderTest: 'grmt-under-test',
  // employeeAnalytics: 'employee-analytics',
  // partnerTrigger: 'partner-trigger',
  explore: 'explore',
  statistics: 'statistics'
  // insights: 'insights'
};

export const viewTabs = [
  { key: views.liveDashboard, title: 'Live Dashboard', enabled: true },
  // DISABLED - only the Live Dashboard tab ships; see the note above.
  // { key: views.grmtUnderTest, title: 'GRMT Under Test', enabled: true },
  // { key: views.employeeAnalytics, title: 'Employee Analytics', enabled: true },
  // { key: views.partnerTrigger, title: 'Partner & Trigger', enabled: true },
  { key: views.explore, title: 'Explore', enabled: true },
  { key: views.statistics, title: 'Statistics', enabled: true }
  // { key: views.insights, title: 'Insights', enabled: true }
];

/**
 * The configuration screen lives at /risk-weekly-report/configuration, a sibling page rather
 * than a seventh report tab: it edits the configuration the report is computed from, which is a
 * different job with a different audience from reading the numbers.
 */
export const RISK_WEEKLY_CONFIG_PATH = 'configuration';

/** Areas of the configuration, matching the backend's RiskWeeklyConfigArea names. */
export const configAreas = {
  settingsMap: 'settings-map',
  triggerMap: 'trigger-map',
  partnerMap: 'partner-map',
  reportRouting: 'report-routing',
  departments: 'departments',
  priorities: 'priorities',
  reportRows: 'report-rows',
  timeBuckets: 'time-buckets',
  slaMatrix: 'sla-matrix',
  partners: 'partners',
  employees: 'employees',
  reportTargets: 'report-targets',
  // Which saved filter feeds which lane; the filters themselves are read from dbo.TableSettings.
  reportLanes: 'report-lanes'
};

/** Screens of the configuration page, in the order they earn their place. */
export const configViews = {
  mappingQueue: 'mapping-queue',
  valueMaps: 'value-maps',
  partners: 'partners',
  employees: 'employees',
  structure: 'structure',
  documents: 'documents'
};

export const configTabs = [
  {
    key: configViews.mappingQueue,
    title: 'Mapping Queue',
    hint: 'Values the report could not classify. While this is non-empty the numbers are incomplete.'
  },
  {
    key: configViews.valueMaps,
    title: 'Value Maps',
    hint: 'Settings, triggers and partners routed to a department, priority and report row.'
  },
  { key: configViews.partners, title: 'Partners', hint: 'Partner to brand — the B2B/B2C discriminator.' },
  { key: configViews.employees, title: 'Employees', hint: 'Analyst roster: home department and brand.' },
  {
    key: configViews.structure,
    title: 'Routing & SLA',
    hint: `Report routing, the four vocabularies every other area picks from, and the SLA verdict
           grid.`
  },
  {
    key: configViews.documents,
    title: 'Targets & Lanes',
    hint: 'Report targets as a form, and the export lanes read from the saved filters.'
  }
];

/** The three value maps, as the Value Maps tab presents them. */
export const valueMapTabs = [
  { key: configAreas.settingsMap, title: 'Settings' },
  { key: configAreas.triggerMap, title: 'Triggers' },
  { key: configAreas.partnerMap, title: 'Partners' }
];

/** Page size for the configuration lists. Settings-map alone is 174 rows. */
export const configPageSize = 50;

/**
 * How old the partner list's newest change may be before the dashboard flags it.
 *
 * Named for the lanes, which used to be a transcription with the same decay. They are read live
 * from the saved filters since 29.09.2026 and no longer use it.
 */
export const laneCaptureStaleDays = 90;

/**
 * What configures each block of the Live Dashboard.
 *
 * The report is computed from fourteen configuration areas and shows none of them, which is the
 * single most common question it produces: "why is this number what it is". Each entry names
 * what a setting DECIDES rather than what it is called, because the reader is looking at a chart
 * and not at a schema.
 *
 * `tab` is the configuration screen to open. `local: true` marks a value that lives in this file
 * instead of the database — stated rather than hidden, since those are exactly the ones a
 * configuration screen cannot explain.
 */
export const configSources = {
  weeklyVolume: {
    title: 'Total Alerts by Department',
    summary: `One stacked band per department, counting alerts by the week they were CREATED —
              not the week anybody worked on them. A department with no alerts in a week is
              absent from that week rather than drawn as a zero.`,
    items: [
      {
        what: 'Which alerts are collected at all',
        area: 'export lanes · saved filters',
        tab: configViews.documents
      },
      // The partner list was missing from every entry here, and it is the outer bound of the
      // chart: a partner absent from it contributes nothing and, having no brand, could not be
      // displayed even if it did. It belongs above the department mapping, since it decides
      // whether a row exists before anything decides where the row goes.
      {
        what: 'Which partners exist at all, and their brand',
        area: 'partners',
        tab: configViews.partners
      },
      { what: 'Department name, colour and order', area: 'departments', tab: configViews.structure },
      { what: 'Which department an alert routes to', area: 'value maps + report routing', tab: configViews.valueMaps }
    ]
  },
  windowSummary: {
    title: 'From → To summary',
    summary: `Totals for the WHOLE selected range, not for one week — each count is the sum of
              the weeks in the window. The two rate tiles are judged against a target and
              coloured pass or fail; the rest are plain counts.`,
    items: [
      {
        what: 'Automation and false-positive targets the tiles are judged against',
        area: 'report-targets',
        tab: configViews.documents
      },
      { what: 'Alerts excluded as Test', area: 'report-targets · excludedPriorities', tab: configViews.documents }
    ]
  },
  rateTrend: {
    title: 'Automation, false positives and SLA compliance',
    summary: `The same three rates as the tiles above, drawn week by week so a direction is
              visible rather than a single figure. The flat horizontal lines are the targets,
              not data.`,
    items: [
      { what: 'The target lines', area: 'report-targets', tab: configViews.documents },
      { what: 'Verdict behind the SLA series', area: 'sla-matrix', tab: configViews.structure }
    ]
  },
  departmentTable: {
    title: 'Department breakdown',
    summary: `One row per department for the LATEST week of the window — not for the window as
              a whole. Counts and rates sit side by side, and the "%" suffix in a column name is
              the only thing separating them.`,
    items: [
      { what: 'Department name, colour and order', area: 'departments', tab: configViews.structure },
      { what: 'Which department an alert routes to', area: 'value maps + report routing', tab: configViews.valueMaps },
      {
        what: 'Which brand a partner belongs to — and so which routing rule, and which priority',
        area: 'partners',
        tab: configViews.partners
      }
    ]
  },
  slaCompliance: {
    title: 'Compliance to Processing SLA',
    summary: `How much of each department's manual work met its SLA. Cascaded closures are
              excluded from both sides of the fraction: nobody was answerable for a closure the
              system performed on their behalf.`,
    items: [
      { what: 'Where the resolution time falls', area: 'time-buckets', tab: configViews.structure },
      { what: 'Verdict for a priority × bucket pair', area: 'sla-matrix', tab: configViews.structure },
      { what: 'Which priority a department is judged at', area: 'report routing', tab: configViews.structure },
      {
        what: 'The green / amber / red bands on the tiles — NOT the targets above, and a different scale',
        area: 'bandThresholds, in the client',
        local: true
      }
    ]
  },
  drilldown: {
    title: 'Alert drilldown',
    summary: `The rows behind one department's number: each analyst's manual decisions, then the
              individual alerts with status, priority, elapsed time and SLA verdict. The list is
              capped per week, breaches are sorted first, and the total says what the cap hid.`,
    items: [
      { what: 'Analyst name and home department', area: 'employees', tab: configViews.employees },
      { what: 'How many alerts are listed per week', area: 'report-targets · alertCapPerWeek', tab: configViews.documents },
      { what: 'Which partners are read at all', area: 'partners', tab: configViews.partners }
    ]
  },
  partnerCoverage: {
    title: 'Partner coverage',
    summary: `Which partners contributed to this window, and which are missing from the
              configuration altogether. A partner absent from the list contributes nothing and,
              having no brand, could not be displayed even if it did.`,
    items: [
      {
        what: 'The list this checks — the report reads no partner outside it',
        area: 'partners',
        tab: configViews.partners
      },
      {
        what: 'Which partners each lane names, by list or by platform tag',
        area: 'export lanes · saved filters',
        tab: configViews.documents
      }
    ]
  }
};

/**
 * What each headline metric means, for the tooltip beside it.
 *
 * The footer already lists the formulas; this exists because a formula is not the part people
 * get wrong. What they get wrong is the DENOMINATOR: Automation Rate is measured against every
 * closure including cascades, False Positive Rate against human decisions only. The two
 * percentages therefore sit side by side on one strip and cannot be added, subtracted or
 * compared — and nothing on the screen said so.
 *
 * `watch` is the sentence worth reading before acting on the number.
 */
export const metricHelp = {
  automation: {
    label: 'Automation Rate',
    what: 'Of the alerts that reached a decision, the share the system decided by itself.',
    formula: 'system ÷ monitored',
    denominator: 'Total Monitored — every Confirmed or Rejected alert, cascaded closures INCLUDED.',
    watch: `Not the denominator False Positive Rate uses. The two rates on this strip are not
            comparable with each other.`
  },
  falsePositive: {
    label: 'False Positive Rate',
    what: 'Of the alerts a person actually judged, the share they rejected as not a real threat.',
    formula: 'rej_manual ÷ manual',
    denominator: 'Manual decisions only, cascaded closures EXCLUDED — a cascade is automation, but it is not human work.',
    watch: 'A rise means detection got noisier, not that analysts got stricter.'
  },
  notChecked: {
    label: 'Not Checked',
    what: 'Alerts nobody has opened. A count, not a rate.',
    formula: 'Created + ReCreated',
    denominator: '—',
    watch: `Reviewing — opened, no decision yet — is counted here by neither side: it is not
            "not checked" and not "resolved".`
  },
  total: {
    label: 'Total Alerts',
    what: 'Every alert attributed to this window, whatever state it is in.',
    formula: 'all rows in the group',
    denominator: '—',
    watch: `Attributed by the week the alert was CREATED. Work done this week on an older alert
            counts against that older week, not this one.`
  },
  monitored: {
    label: 'Total Monitored',
    what: 'Alerts that reached a decision, by a person or by the system.',
    formula: 'Confirmed + Rejected',
    denominator: '—',
    watch: 'This is the denominator of Automation Rate, shown so the rate above can be checked.'
  },
  sla: {
    label: 'SLA Compliance',
    what: 'Of the decisions carrying an SLA verdict, the share that met it.',
    formula: 'sla_c ÷ tagged',
    denominator: 'Manual decisions that carry a verdict. Cascades carry none, so they are in neither half.',
    watch: `The verdict comes from priority × elapsed-time bucket, so the same hour passes at one
            priority and fails at another.`
  },
  coResolution: {
    label: 'Co-resolution',
    what: `Of the closures in multi-account batches, the share the system cascaded after an
           analyst decided one linked alert.`,
    formula: 'coresolved ÷ (coresolved + kept decisions)',
    denominator: 'Cascaded closures plus the one kept decision of each batch.',
    watch: 'High is not bad — this is what stops one human decision being counted as forty.'
  }
};

/**
 * What each configuration screen decides, and what an error in it costs.
 *
 * Shown inline on the configuration page rather than as a tooltip, because this is the thing a
 * person needs BEFORE they edit, not after they hover. The levels are the ones the
 * documentation uses: 1 selects which alerts arrive, 2 classifies them, 3 judges and draws
 * them. The asymmetry is the point — an error at level 2 does not show up as a wrong number,
 * it shows up as no number at all.
 */
export const configAreaHelp = {
  [configViews.mappingQueue]: {
    level: '2',
    decides: 'Nothing by itself. It lists the values that no map could classify.',
    error: `Everything listed here is alerts that exist in the platform and appear nowhere on the
            dashboard. Not "other", not "unknown" — absent, with no total reporting the gap.`,
    detail: `While this queue is non-empty the report is incomplete by exactly the volume behind
             these values. Mapping one entry is the cheapest correctness fix this page offers.`
  },
  [configViews.valueMaps]: {
    level: '2',
    decides: `Which department, priority and report row an alert gets — looked up by its GRMT
              setting version, its trigger, or its partner.`,
    error: 'A missing row removes the alert from every surface. A wrong row moves real volume to the wrong department.',
    detail: `The Settings tab lists every GRMT setting from Alert Settings. An entry is stored
             against a setting VERSION, but a new version inherits what an earlier version's entry
             overrode, and a setting with no entry at all follows its own category and priority -
             so only the settings marked Unclassified fall out of the report.`
  },
  [configViews.partners]: {
    level: '1',
    decides: 'Which partners the report reads at all, and whether each one is B2B or B2C.',
    error: `A partner missing here contributes nothing. A partner with the wrong brand moves its
            whole volume into the other column.`,
    detail: `Read from the platform, not configured here: the brand follows dbo.Partner.PartnerTags
             and membership follows dbo.Partner.Enabled, so a fix belongs in the partner's General
             Settings. For the families that have no export lane, this list is the entire scope.`
  },
  [configViews.employees]: {
    level: '3',
    decides: 'The analyst roster: home department and brand.',
    error: 'Display only. An alert is still counted; it is the per-analyst breakdown that changes.',
    detail: `Names are not stored here — the id resolves to the platform user. This is an
             establishment list, not a permission list.`
  },
  [configViews.structure]: {
    level: '2 & 3',
    decides: `Report routing (level 2), plus the vocabularies, the time buckets and the SLA
              verdict grid (level 3).`,
    error: `A missing routing rule makes that whole (family, brand, sub-tag) combination vanish.
            A wrong SLA cell only changes a verdict.`,
    detail: `Routing is two-level on purpose: a rule either names its values directly or defers
             to a map. Collapsing that would discard route-level priority overrides and invent
             SLA failures that are not there.`
  },
  [configViews.documents]: {
    level: '1 & 3',
    decides: `The export lanes — level 1, which alerts arrive at all — and the target and
              threshold document, level 3.`,
    error: `Lanes decide the population, so a lane mapped to the wrong saved filter under- or
            over-reports with no sign at all. Targets only change how a number is coloured.`,
    detail: `The lanes are read live from the lane operator's saved filters; what is edited here
             is only which filter feeds which lane. Both documents are part of the one
             configuration document, so each save is a new version with its own history.`
  }
};

export const brands = {
  both: 'Both',
  b2b: 'B2B',
  b2c: 'B2C'
};

export const brandOptions = [brands.both, brands.b2b, brands.b2c];

/** Mirrors web_data.py DEPT_COLORS / DEPT_FALLBACK_COLOR. */
export const departmentColors = {
  sportsbook: '#f5d547',
  // Sportsbook is displayed as two halves; the keys are what dept_key() makes of
  // "Sportsbook | Linked Accounts" and "Sportsbook | Other".
  sportsbookli: '#f5d547',
  sportsbookot: '#B2BAE0',
  kyc: '#9A64FF',
  casino: '#8A95D6',
  payments: '#6613FF',
  ops: '#7F7F7F',
  traffic: '#4d94ce'
};

/**
 * Banner colour for a parent that is only shown as its halves — a blend of the two children's
 * purples, so the outer band reads as their sum rather than as a third department.
 */
export const departmentParentColors = {
  Sportsbook: '#4051B5'
};

export const departmentParentFallbackColor = '#4051B5';

export const departmentFallbackColor = '#7e93b0';

/**
 * How many weeks the report opens on: the latest this many COMPLETE weeks of the week list.
 *
 * It replaced a fixed range (09.07 - 15.07 to 30.07 - 05.08) that the port was once read against,
 * and which kept the page opening on July long after that comparison was over. Four is also the
 * server's own trailing default (RiskWeeklyOverviewAssembler.DefaultWindowWeeks); the page names
 * the range itself so the dropdowns show it rather than an empty "all weeks".
 */
export const defaultWeekCount = 4;

/** SLA verdicts in display order. */
export const slaOrder = ['Compliant', 'Next segment', 'Huge delay'];

/** Taken verbatim from dashboard.py SLA_COLORS so the segments match the original. */
export const slaColors = {
  Compliant: '#3fa34d',
  'Next segment': '#f4b6c2',
  'Huge delay': '#d62728'
};

/**
 * Band thresholds for the per-department KPI strip, verbatim from dashboard.py
 * ICON_THRESHOLDS / ICON_COLORS. Ratios are 0..1; "Not Checked" is an absolute count.
 */
export const bandThresholds = {
  Automation: { good: 0.5, bad: 0.2, direction: 'higher_better' },
  FP: { good: 0.3, bad: 0.6, direction: 'lower_better' },
  'Not Checked': { good: 0, bad: 50, direction: 'lower_better' }
};

export const bandColors = { good: '#3fa34d', watch: '#f5a623', bad: '#d62728' };

// =====================================================================================
// DISABLED below - settings that belong to the five views the first production release does
// not ship.  RMT8993, 2026-09-14; see docs/report_docs/13-live-dashboard-only.md.
// =====================================================================================
// /**
 // * Alert filters in the GRMT setting drilldown, verbatim from dashboard.py's radio.
 // *
 // * Sent to the API as-is, so these strings are a contract with the backend rather than labels —
 // * "False Positive" is the display name for a rejected alert and must not be renamed on one side
 // * only. The first entry is the default.
 // */
// export const grmtStatusFilters = ['All', 'Confirmed', 'False Positive', 'Not-checked'];

/** Co-resolution is informational: automation-like, with no good/bad reading. */
export const coResolutionColor = '#6f42c1';

/**
 * The two last-week headline tiles, coloured as in dashboard.py's render_last_week_tile.
 *
 * Not the band colours: these two are never good or bad on their own. The window strip above
 * them already carries the target comparison, and repeating it here would read as a second,
 * contradicting verdict on the same metric.
 */
export const lastWeekTileColors = { automation: '#3c4a8a', fp: '#d62728' };

/** Only the multi-account departments can cascade, so only they show the tile. */
export const coResolutionDepartments = ['kyc', 'payments', 'ops'];

/**
 * Departments whose banner names the SLA priority they are judged at.
 *
 * Only OPS, and only because the answer differs by brand — B2B routes it Medium, B2C High — so
 * the same department's compliance is measured against two different clocks. The original
 * annotates just this one banner for that reason; every other department has one priority.
 */
export const slaPriorityDepartments = ['ops'];

// /**
 // * Employee Analytics palette, taken verbatim from dashboard.py's EA_* colour maps.
 // *
 // * Deliberately not the Live Dashboard's SLA colours: the original uses a different palette in
 // * this view, and unifying them would make the two views look like the same measurement.
 // */
// export const employeePriorityOrder = ['Critical', 'High', 'Medium', 'Info'];

// export const employeePriorityColors = {
  // Critical: '#E24B4A',
  // High: '#EF9F27',
  // Medium: '#378ADD',
  // Info: '#888780',
  // // Not in the original: it folds unknown priorities into a bucket it never charts. Neutral, so
  // // an unmapped priority is visible without reading as a severity.
  // Other: '#b9bec7'
// };

// export const employeeSlaColors = {
  // Compliant: '#1D9E75',
  // 'Next segment': '#EF9F27',
  // 'Huge delay': '#E24B4A'
// };

// /** Keyed on the label the API sends — a rejection is a false positive in this view's wording. */
// export const employeeOutcomeColors = {
  // Confirmed: '#290074',
  // 'False Positive': '#F0AED1'
// };

// export const employeeAccentColor = '#7F77DD';

// /** The original's mode radio. The first entry is the default. */
// export const employeeModes = [
  // { key: 'department', title: 'By department' },
  // { key: 'employees', title: 'All employees' }
// ];

// /** Partner & Trigger grains. Sent to the API as the `grain` field; the first is the default. */
// export const partnerTriggerGrains = [
  // { key: 'partner', title: 'Partner' },
  // { key: 'trigger', title: 'Trigger' }
// ];

// /**
 // * Low-sample cutoff for Partner & Trigger, mirroring the original's slider (0-100, step 5,
 // * default 20). Rows with fewer manual decisions than this are hidden, because a confirm rate over
 // * three decisions is noise presented as a measurement.
 // */
// export const partnerTriggerMinChecked = { min: 0, max: 100, step: 5, initial: 20 };

/**
 * Explore — the entity grain. Sent to the API as `grain`; the first entry is the default.
 *
 * Department is always a cube dimension, so this only selects whether a FINER dimension is added.
 */
export const exploreGrains = [
  { key: 'department', title: 'Department', entity: null },
  { key: 'partner', title: 'Partner', entity: 'partner' },
  { key: 'trigger', title: 'Trigger', entity: 'trigger' }
];

export const exploreChartTypes = [
  { key: 'line', title: 'Line (trend)' },
  { key: 'bar', title: 'Bar (compare)' },
  { key: 'scatter', title: 'Scatter (volume vs rate)' }
];

/** The sentinel for "no series split", matching the original's "(none)" option. */
export const exploreNoSeries = '(none)';

/**
 * Count measures, in the original's cube order. `key` is the component field as the API sends it;
 * `id` is the cube column name the original shows, kept so a saved chart reads the same in both.
 */
export const exploreCountMeasures = [
  { id: 'alerts', key: 'total', title: 'alerts' },
  { id: 'resolved_all', key: 'monitored', title: 'resolved_all' },
  { id: 'checked', key: 'manual', title: 'checked' },
  { id: 'confirmed', key: 'conf_manual', title: 'confirmed' },
  { id: 'rejected', key: 'rej_manual', title: 'rejected' },
  { id: 'system', key: 'system', title: 'system' },
  { id: 'coresolved', key: 'coresolved', title: 'coresolved' },
  { id: 'ma_decisions', key: 'ma_dec', title: 'ma_decisions' },
  { id: 'not_checked', key: 'nc', title: 'not_checked' },
  { id: 'sla_tagged', key: 'tagged', title: 'sla_tagged' },
  { id: 'sla_compliant', key: 'sla_c', title: 'sla_compliant' }
];

/**
 * Rate measures. Each names the metrics.js function that defines it and the component field that
 * is its denominator — the formula itself is NOT restated here.
 *
 * The denominator is named separately for one reason: a rate over an empty denominator has no
 * value, and metrics.js returns 0 for it, which on a trend line would draw a week with no manual
 * decisions as a flawless 0% false-positive week. Explore needs to leave that point out entirely,
 * as the original's NaN does.
 */
export const exploreRateMeasures = [
  { id: 'automation_%', fn: 'automationRate', denominator: 'monitored' },
  { id: 'confirm_%', fn: 'confirmRate', denominator: 'manual' },
  { id: 'fp_%', fn: 'falsePositiveRate', denominator: 'manual' },
  { id: 'cores_%', fn: 'coResolutionRate', denominator: 'coresolvedPlusMaDec' },
  { id: 'sla_%', fn: 'slaComplianceRate', denominator: 'tagged' }
];

/**
 * Quick starts, verbatim from the original's _EXPLORE_PRESETS. Each sets a complete, internally
 * consistent control state so a click always produces a chart that renders.
 */
export const explorePresets = [
  {
    title: 'Automation trend by dept',
    state: { grain: 'department', chartType: 'line', x: 'week', measure: 'automation_%', series: 'dept' }
  },
  {
    title: 'FP % compared by dept',
    state: { grain: 'department', chartType: 'bar', x: 'dept', measure: 'fp_%', series: 'brand' }
  },
  {
    title: 'Noisy partners (volume × reject)',
    state: { grain: 'partner', chartType: 'scatter', scatterX: 'checked', scatterY: 'fp_%', scatterColor: 'dept' }
  },
  {
    title: 'Co-resolution trend by dept',
    state: { grain: 'department', chartType: 'line', x: 'week', measure: 'cores_%', series: 'dept' }
  },
  {
    title: 'SLA compliance by dept',
    state: { grain: 'department', chartType: 'bar', x: 'dept', measure: 'sla_%', series: 'brand' }
  },
  {
    title: 'Alerts by trigger',
    state: { grain: 'trigger', chartType: 'bar', x: 'trigger', measure: 'alerts', series: 'brand' }
  }
];

/**
 * Categorical palette for Explore series. The report's own department colours are used when the
 * series IS department; anything else (brand, partner, trigger, week) has no canonical colour, and
 * borrowing the department palette for it would imply a meaning the colour does not carry.
 */
export const exploreSeriesPalette = [
  '#7F77DD', '#1D9E75', '#EF9F27', '#E24B4A', '#378ADD',
  '#B36AE2', '#57B9C4', '#C2185B', '#8A95D6', '#7F7F7F'
];

/** Rows shown at once in the underlying-cube table. The cube can run to thousands of rows. */
export const exploreCubePageSize = 100;

/**
 * How many categories a bar or scatter chart draws before the tail is folded away. The original
 * plots every partner, which at trigger grain produces an unreadable axis hundreds of items wide.
 */
export const exploreMaxCategories = 30;

// /**
 // * Insights palette, verbatim from dashboard.py INS_DOMAIN_COLOR / INS_DIR_COLOR.
 // *
 // * Domain colours the card's left border; direction colours the executive tile. They are separate
 // * because the two answer different questions — which part of the operation, and is it good or bad.
 // */
// export const insightDomainColors = {
  // Live: '#378ADD',
  // Test: '#7F77DD',
  // Workforce: '#1D9E75'
// };

// export const insightDirectionColors = {
  // improvement: '#1D9E75',
  // deterioration: '#E24B4A',
  // attention: '#EF9F27'
// };

// export const insightFallbackColor = '#888888';

// /** The original's domain radio. "All" is the default and is not a domain the API sends. */
// export const insightDomains = ['All', 'Live', 'Test', 'Workforce'];

// /**
 // * Where each insight's `view` field points. The API sends this report's own view keys, so an
 // * insight can link straight to the view that shows the underlying numbers.
 // */
// export const insightViewLabels = {
  // [views.liveDashboard]: 'Live Dashboard',
  // [views.grmtUnderTest]: 'GRMT Under Test',
  // [views.employeeAnalytics]: 'Employee Analytics'
// };

/**
 * Metric definitions, shown in the UI exactly as the Python sidebar states them. Keeping the
 * wording next to the code makes it obvious when a formula and its description drift apart.
 */
export const metricDefinitions = [
  { label: 'Automation Rate', formula: 'System-resolved ÷ Total Monitored' },
  { label: 'False Positive Rate', formula: 'Rejected (manual) ÷ Manual resolutions' },
  { label: 'Not Checked', formula: 'Created + ReCreated' },
  { label: 'SLA Compliance', formula: 'Compliant ÷ SLA-tagged manual resolutions' },
  { label: 'Co-resolution', formula: 'Cascaded closures ÷ (cascaded + kept decisions)' },
  { label: 'Total Monitored', formula: 'Confirmed + Rejected (all resolutions)' }
];

export default {
  RISK_WEEKLY_MODULE_PATH,
  views,
  viewTabs,
  brands,
  brandOptions,
  configSources,
  configAreaHelp,
  metricHelp,
  laneCaptureStaleDays,
  departmentColors,
  departmentFallbackColor,
  defaultWeekCount,
  departmentParentColors,
  departmentParentFallbackColor,
  slaOrder,
  slaColors,
  // grmtStatusFilters,
  // employeePriorityOrder,
  // employeePriorityColors,
  // employeeSlaColors,
  // employeeOutcomeColors,
  // employeeAccentColor,
  // employeeModes,
  // partnerTriggerGrains,
  // partnerTriggerMinChecked,
  exploreGrains,
  exploreChartTypes,
  exploreNoSeries,
  exploreCountMeasures,
  exploreRateMeasures,
  explorePresets,
  exploreSeriesPalette,
  exploreCubePageSize,
  exploreMaxCategories,
  // insightDomainColors,
  // insightDirectionColors,
  // insightFallbackColor,
  // insightDomains,
  // insightViewLabels,
  metricDefinitions
};
